import { type FunctionComponent, useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ButtonGroup } from '@atlaskit/button';
import Button from '@atlaskit/button/new';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/compiled';

import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import LegalDisclaimer from './LegalDisclaimer/LegalDisclaimer';

const messages = defineMessages({
  accept: {
    id: 'footer.accept',
    defaultMessage: 'Accept',
    description:
      'Button to consent for this app to have the listed permissions',
  },
  cancel: {
    id: 'footer.cancel',
    defaultMessage: 'Cancel',
    description:
      'Button to cancel the consent for this app to have the listed permissions',
  },
  permissionsSelected: {
    id: 'oauth.twg.footer.permissionsSelected',
    defaultMessage:
      '{selectedCount} out of {totalCount, plural, one {# permission} other {# permissions}} selected',
    description:
      'Footer count shown while TWG CLI permissions are being edited',
  },
});

export const TWG_FOOTER_MIN_HEIGHT = '88px';

const footerStyle = xcss({
  bottom: 'space.0',
  backgroundColor: 'elevation.surface',
  borderTopColor: 'color.border',
  borderTopStyle: 'solid',
  borderTopWidth: 'border.width',
  boxSizing: 'border-box',
  flexShrink: 0,
  left: 'space.0',
  minHeight: TWG_FOOTER_MIN_HEIGHT,
  padding: 'space.300',
  position: 'fixed',
  right: 'space.0',
  width: '100%',
  zIndex: 'navigation',
});

const footerInnerStyle = xcss({
  margin: '0 auto',
  maxWidth: '1280px',
  width: '100%',
});

const disclaimerWideOnlyStyle = xcss({
  display: 'none',
  [media.above.md]: {
    display: 'flex',
  },
});

const disclaimerNarrowOnlyStyle = xcss({
  display: 'block',
  marginTop: 'space.150',
  textAlign: 'right',
  [media.above.md]: {
    display: 'none',
  },
});

const disclaimerSlotStyle = xcss({
  flex: '1 1 280px',
  minWidth: '0',
});

const actionsStyle = xcss({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 'space.200',
  justifyContent: 'flex-end',
  width: '100%',
  [media.above.md]: {
    gap: 'space.400',
    width: 'auto',
  },
});

type ButtonDecision = 'accept' | 'cancel';

export interface TwgFooterProps {
  acceptDisabled?: boolean;
  selectedPermissionsSummary?: {
    selectedCount: number;
    totalCount: number;
  };
}

const TwgFooter: FunctionComponent<TwgFooterProps> = ({
  acceptDisabled = false,
  selectedPermissionsSummary,
}) => {
  const analytics = useAnalytics();
  const [clickedButton, setClickedButton] = useState<ButtonDecision | null>(
    null,
  );

  const fireAnalyticEvent = useCallback(
    (buttonName: string, buttonValue: ButtonDecision) => () => {
      analytics.sendButtonClickEvent(buttonName);
      setTimeout(() => setClickedButton(buttonValue), 0);
    },
    [analytics],
  );

  const buttons = (
    <ButtonGroup>
      <Button
        type="submit"
        appearance="subtle"
        name="decision"
        value="cancel"
        onClick={fireAnalyticEvent('cancelConsent', 'cancel')}
        isDisabled={clickedButton !== null}
      >
        <FormattedMessage {...messages.cancel} />
      </Button>
      <Button
        type="submit"
        appearance="primary"
        name="decision"
        value="accept"
        onClick={fireAnalyticEvent('acceptConsent', 'accept')}
        isDisabled={acceptDisabled || clickedButton !== null}
      >
        <FormattedMessage {...messages.accept} />
      </Button>
    </ButtonGroup>
  );

  return (
    <Box xcss={footerStyle}>
      <Flex
        xcss={footerInnerStyle}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        wrap="wrap"
        gap="space.400"
      >
        <Box xcss={disclaimerWideOnlyStyle}>
          <Box xcss={disclaimerSlotStyle}>
            <LegalDisclaimer />
          </Box>
        </Box>
        <Box xcss={actionsStyle}>
          {selectedPermissionsSummary && (
            <Text as="span" color="color.text.subtle">
              <FormattedMessage
                {...messages.permissionsSelected}
                values={selectedPermissionsSummary}
              />
            </Text>
          )}
          {buttons}
        </Box>
      </Flex>
      <Box xcss={disclaimerNarrowOnlyStyle}>
        <LegalDisclaimer />
      </Box>
    </Box>
  );
};

export default TwgFooter;
